import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import type { FC, ReactNode } from "react";
import type { RootState } from "@store/store";

interface OtpVerificationGuardProps {
	children: ReactNode;
}

/**
 * Guard that prevents access to routes when OTP verification is pending after signup
 * Only applies to authenticated users - unauthenticated users are handled by AuthGuard
 * Redirects to OTP verification page if user hasn't verified OTP yet
 * Allows access to OTP page, signup, login, and public routes
 */
const OtpVerificationGuard: FC<OtpVerificationGuardProps> = ({ children }) => {
	const location = useLocation();
	const { isAuthenticated } = useSelector((state: RootState) => state.user);
	const otpVerificationSource = localStorage.getItem("otpVerificationSource");
	const otpEmail = localStorage.getItem("otpEmail");

	// List of allowed routes when OTP verification is pending
	const allowedRoutes: readonly string[] = [
		BROWSER_ROUTES.VERIFY_OTP,
		BROWSER_ROUTES.SIGNUP,
		BROWSER_ROUTES.LOGIN,
		BROWSER_ROUTES.FORGET_PASSWORD,
		BROWSER_ROUTES.PRIVACY_POLICY,
		BROWSER_ROUTES.TERMS_AND_CONDITIONS,
	] as const;

	// Only check OTP verification for authenticated users
	// Unauthenticated users are handled by AuthGuard
	if (isAuthenticated && otpVerificationSource === "signup" && otpEmail) {
		// Allow access to OTP page and other public routes
		if (allowedRoutes.includes(location.pathname)) {
			return <>{children}</>;
		}
		// Redirect to OTP page for all other routes
		return <Navigate to={BROWSER_ROUTES.VERIFY_OTP} replace />;
	}

	return <>{children}</>;
};

export default OtpVerificationGuard;

